import LoadingSpinner from "@/components/LoadingSpinner"
import { useCreateEvent } from "@/features/events/hooks/useCreateEvent"
import { EventType as NewEventType } from "@/features/events/types/EventType"
import { HealthPlanSection } from "@/features/health-plan/components/HealthPlanSection"
import MealPlanPreview from "@/features/meal-plan/components/MealPlanPreview"
import { useGetMealPlans } from "@/features/meal-plan/hooks/useGetMealPlans"
import FolderCard from "@/features/shared/components/FolderCard"
import HealthAssessmentSection from "@/pages/new-platform/health-plan/components/HealthAssessmentSection"
import { useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useIsMetabolicUser } from "../../../features/metabolic-health/hooks/useMetabolicHealth"
import { saveEvent } from "../../../utils/apis/user-service"
import { keyQuestionsText } from "../../../utils/Constants"
import { EventType } from "../../../utils/enums/EventType"
import { useAuthStore } from "../../../utils/stores/useAuthStore"
import { useSurveyDataStore } from "../../../utils/stores/useSurveyDataStore"
import { useUserStore } from "../../../utils/stores/useUserStore"
import { useGetHealthPlan } from "../hooks/useGetHealthPlan"

const HealthPlan = () => {
    const { healthPlans, isLoading, refetch: refetchHealthPlan } = useGetHealthPlan()
    const { mealPlans, isLoading: isMealPlanLoading, refetch: refetchMealPlan} = useGetMealPlans()
    const { sendEvent } = useCreateEvent()
    const { auth } = useAuthStore()
    const idToken = auth.idToken
    const userId = auth.userId
    const { userData } = useUserStore()
    const email = userData.email
    const { surveyData } = useSurveyDataStore()
    const healthAssessmentAnswers = surveyData.healthAssessmentAnswers
    const keyQuestionsIds = surveyData.keyQuestions
    const allAnswers = surveyData.allAnswers
    // const healthPlans = plans.healthPlans
    const navigate = useNavigate()
    const isMetabolicUser = useIsMetabolicUser()

    const keyParameters = useMemo(() => {
        const parameters = [
            { emoji: "🎯", name: "Obiettivo" },
            { emoji: "💧", name: "Acqua" },
            { emoji: "⚖️", name: "Peso" },
            { emoji: "🛌", name: "Sonno" },
            { emoji: "⚡", name: "Energia" },
            // { emoji: "🫃", name: "Digestione" },
            { emoji: "💪", name: "Esercizio" },
        ];

        if (!keyQuestionsIds || !healthAssessmentAnswers || !allAnswers) {
            return parameters.map(param => ({ ...param, questionId: "", value: [""] }));
        }

        return parameters.map((param, index) => {
            const questionId = keyQuestionsIds[index];
            const questionText = keyQuestionsText[index]

            if (!questionId) {
                return { ...param, questionId: "", value: [""] };
            }

            // const answer = healthAssessmentAnswers.find(a => a.id === questionId)?.answers || [""];
            const answer = allAnswers.find(a => a.question_text === questionText)?.answers || [""]

            return { ...param, questionId, value: answer };
        });
    }, [healthAssessmentAnswers, keyQuestionsIds]);

    // Polling function
    const startPolling = () => {
        const intervalId = setInterval(() => {
            refetchHealthPlan();
            refetchMealPlan()
        }, 60000);

        return intervalId;
    };

    const stopPolling = (intervalId: NodeJS.Timeout) => {
        clearInterval(intervalId);
    };

    useEffect(() => {
        if (!email || !userId || !idToken) return

        saveEvent(
            email,
            EventType.PageView,
            (success) => { console.log() },
            (error) => { console.log(error) },
            "health_plan_page"
        )

        healthPlans && saveEvent(
            email,
            EventType.PageView,
            (success) => { console.log() },
            (error) => { console.log(error) },
            "health_plan_page_with_health_plan"
        )

        const intervalId = startPolling();
        return () => {
            stopPolling(intervalId);
        };
    }, [email, auth.userId, auth.idToken, healthPlans, refetchHealthPlan]);

    useEffect(() => {
        void sendEvent({
            event: NewEventType.PAGE_VIEW,
            sub_category: "health_plan_page",
            event_metadata: { health_plan_available: healthPlans !== null}
        })
    }, []);

    // ********************************************************************************

    if (isLoading) {
        return (
            <LoadingSpinner />
        )
    }

    return (
        <div className="w-full h-full flex flex-col space-y-[20px] max-w-[1068px]">
            {/* <div className="h-[40px] flex items-center">
                <div className="font-bold text-[25px] leading-[33px]">
                    Health Plan
                </div>
            </div> */}
            <div className="space-y-[20px]">
                {
                    healthPlans ?
                        <HealthPlanSection healthPlan={healthPlans[0]} />
                        :
                        <div className="mb-[20px]">
                            Dopo aver effettuato il consulto con il medico potrai vedere riassunte qui le indicazioni ricevute.
                        </div>
                }
                {
                    mealPlans &&
                    <MealPlanPreview
                        mealPlan={mealPlans[0]}
                        healthPlan={healthPlans ? healthPlans[0] : null}
                    />
                }
                {
                    (healthAssessmentAnswers && keyQuestionsIds && !isMetabolicUser) &&
                    <HealthAssessmentSection keyParameters={keyParameters} />
                }
                {
                    healthPlans && healthPlans.length > 1 &&
                    <div className="space-y-[20px]">
                        <div className="w-full h-[1px] bg-black" />
                            <FolderCard
                                onClick={() => navigate("/health-plan-history")}
                                title={"Storico Health Plan"}
                        />
                    </div>
                }
            </div>
            <div className="p-[10px]" />
        </div>
    )
}

export default HealthPlan
